import { normalizeEmployeeType } from './employeeTypeLabels';
import { buildApiUrl } from './apiSetup';

/**
 * Build employee-master API payload from form state.
 */
export function buildEmployeeMasterPayload(formData, masters, departments) {
  const selectedDept = departments.find((d) => String(d.id) === String(formData.department_id));
  const selectedGrade = masters?.grades?.find(
    (g) => String(g.code) === String(formData.scale) || String(g.value) === String(formData.grade_master_id)
  );
  const selectedType = masters?.employeeTypes?.find(
    (t) => String(t.value) === String(formData.employee_type_master_id)
  );

  const defaultUserRole = masters?.roles?.find(
    (r) => r.slug === 'user' || r.name?.toLowerCase() === 'user' || String(r.label).toLowerCase() === 'user'
  );

  const roleOption = masters?.roles?.find(
    (r) => String(r.value) === String(formData.hospital_role_id) || r.slug === formData.hospital_role
  ) || defaultUserRole;

  const designation = masters?.designations?.find(
    (d) => String(d.value) === String(formData.designation_id)
  );

  const branch = masters?.branches?.find((b) => String(b.value) === String(formData.branch_id));

  const payload = {
    emp_code: formData.emp_code || undefined,
    first_name: formData.first_name,
    middle_name: formData.middle_name || undefined,
    last_name: formData.last_name,
    email: formData.email || formData.official_email || undefined,
    personal_email: formData.personal_email || undefined,
    official_email: formData.official_email || formData.email || undefined,
    phone: formData.phone || undefined,
    alternate_phone: formData.alternate_phone || undefined,
    employee_type: formData.employee_type || 'permanent',
    employee_type_master_id: formData.employee_type_master_id
      ? parseInt(formData.employee_type_master_id, 10)
      : selectedType?.value || undefined,
    department_id: formData.department_id ? parseInt(formData.department_id, 10) : undefined,
    designation_id: formData.designation_id ? parseInt(formData.designation_id, 10) : undefined,
    designation_type: formData.designation_type || undefined,
    office_location_id: formData.office_location_id ? parseInt(formData.office_location_id, 10) : undefined,
    branch_id: formData.branch_id ? parseInt(formData.branch_id, 10) : undefined,
    location_detail_id: formData.location_detail_id ? parseInt(formData.location_detail_id, 10) : undefined,
    hospital_role_id: formData.hospital_role_id
      ? parseInt(formData.hospital_role_id, 10)
      : roleOption?.value || undefined,
    grade_master_id: formData.grade_master_id
      ? parseInt(formData.grade_master_id, 10)
      : selectedGrade?.value || undefined,
    reporting_manager_id: formData.reporting_manager_id
      ? parseInt(formData.reporting_manager_id, 10)
      : undefined,
    supervisor_id: formData.supervisor_id ? parseInt(formData.supervisor_id, 10) : undefined,
    functional_manager_id: formData.functional_manager_id
      ? parseInt(formData.functional_manager_id, 10)
      : undefined,
    join_date: formData.join_date,
    date_of_birth: formData.date_of_birth || undefined,
    sex: formData.sex || undefined,
    marital_status: formData.marital_status || undefined,
    blood_group: formData.blood_group || undefined,
    sec_code: selectedDept?.code || formData.sec_code || 'HO',
    scale: (masters?.scales?.find(s => String(s.id) === String(formData.scale_id))
      ? `${masters.scales.find(s => String(s.id) === String(formData.scale_id)).code || masters.scales.find(s => String(s.id) === String(formData.scale_id)).name} ( ${masters.scales.find(s => String(s.id) === String(formData.scale_id)).min_basic ?? 0} - ${masters.scales.find(s => String(s.id) === String(formData.scale_id)).max_basic ?? 0} )`
      : undefined) || formData.scale || formData.pay_scale,
    scale_id: formData.scale_id ? parseInt(formData.scale_id, 10) : undefined,
    basic_pay: formData.basic_pay ? parseFloat(formData.basic_pay) : undefined,
    emp_desig: designation?.label || formData.emp_desig,
    shift_id: formData.shift_id ? parseInt(formData.shift_id, 10) : undefined,
    hospital_branch: branch?.name || formData.hospital_branch || undefined,
    work_location: formData.work_location || undefined,
    current_address: formData.current_address || '',
    permanent_address: formData.permanent_address || '',
    city: formData.city || '',
    state: formData.state || '',
    pin_code: formData.pin_code || '000000',
    pan_number: formData.pan_number || undefined,
    aadhaar_number: formData.aadhaar_number || undefined,
    pension_scheme_type: formData.pension_scheme_type || undefined,
    pf_number: formData.pension_scheme_type === 'GPF' ? (formData.emp_code || undefined) : null,
    pran_number: formData.pension_scheme_type === 'NPS' ? (formData.pran_number || undefined) : null,
    uan_number: formData.uan_number || undefined,
    esic_number: formData.esic_number || undefined,
    pf_code: formData.pf_code || undefined,
    hba_id: formData.hba_id || undefined,
    opening_pf_balance: formData.opening_pf_balance !== '' && formData.opening_pf_balance !== undefined ? parseFloat(formData.opening_pf_balance) : 0,
    opening_vpf_balance: formData.opening_vpf_balance !== '' && formData.opening_vpf_balance !== undefined ? parseFloat(formData.opening_vpf_balance) : 0,
    opening_pf_balance_as_on: formData.opening_pf_balance_as_on || undefined,
    is_physically_disabled: formData.is_disabled === 'Yes',
    is_eligible_washing_allowance: !!formData.is_eligible_washing_allowance,
    is_eligible_family_planning_allowance: !!formData.is_eligible_family_planning_allowance,
    payroll_head_id: formData.payroll_head_id ? parseInt(formData.payroll_head_id, 10) : undefined,
    is_eligible_conveyance_allowance: !!formData.is_eligible_conveyance_allowance,
    bank_name: formData.bank_name || undefined,
    bank_id: formData.bank_id ? parseInt(formData.bank_id, 10) : undefined,
    bank_account_number: formData.bank_account_number || undefined,
    bank_account_name: formData.bank_account_name || undefined,
    bank_ifsc_code: formData.bank_ifsc_code || undefined,
    bank_branch: formData.bank_branch || undefined,
    bank_branch_code: formData.bank_branch_code || undefined,
    emergency_contact_name: formData.emergency_contact_name || undefined,
    emergency_contact_number: formData.emergency_contact_number || undefined,
    emergency_contact_relationship: formData.emergency_contact_relationship || undefined,
    father_name: formData.father_name || undefined,
    mother_name: formData.mother_name || undefined,
    employment_status: formData.employment_status || 'probation',
    submit_for_approval: !!formData.submit_for_approval,
    org_mapping: {
      shift_group_id: formData.shift_group_id ? parseInt(formData.shift_group_id, 10) : undefined,
      roster_group_id: formData.roster_group_id ? parseInt(formData.roster_group_id, 10) : undefined,
      payroll_group_id: formData.payroll_group_id ? parseInt(formData.payroll_group_id, 10) : undefined,
      leave_policy_master_id: formData.leave_policy_master_id
        ? parseInt(formData.leave_policy_master_id, 10)
        : undefined,
      attendance_policy_master_id: formData.attendance_policy_master_id
        ? parseInt(formData.attendance_policy_master_id, 10)
        : undefined,
      hospital_role_id: formData.hospital_role_id ? parseInt(formData.hospital_role_id, 10) : undefined,
    },
  };

  Object.keys(payload.org_mapping).forEach((k) => {
    if (payload.org_mapping[k] === undefined) delete payload.org_mapping[k];
  });
  if (Object.keys(payload.org_mapping).length === 0) delete payload.org_mapping;

  return payload;
}

export async function uploadPendingDocuments(employeeId, pendingDocuments, otherDocuments, token) {
  const results = [];
  
  // Upload mandatory documents
  for (const item of pendingDocuments) {
    if (!item.file || !item.document_type_id) continue;
    
    // Validate document_type_id is a number
    const numericId = Number(item.document_type_id);
    if (isNaN(numericId) || numericId <= 0) {
      results.push({ error: 'Invalid document type ID', type_id: item.document_type_id });
      continue;
    }
    
    // Skip fallback ID 999 (Passbook) if not supported by backend
    if (numericId === 999) {
      results.push({ warning: 'Passbook document skipped - backend document type not configured', type_id: numericId });
      continue;
    }
    
    const fd = new FormData();
    fd.append('file', item.file);
    const params = new URLSearchParams({ document_type_id: numericId.toString() });
    if (item.document_number) params.set('document_number', item.document_number);
    if (item.expiry_date) params.set('expiry_date', item.expiry_date);
    const res = await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/documents?${params.toString()}`),
      {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      results.push({ error: err.detail || 'Upload failed', type_id: numericId });
    } else {
      results.push(await res.json());
    }
  }
  
  // Upload other documents - always use document_type_id = 13 (OTHER)
  const OTHER_DOCUMENT_TYPE_ID = 13;
  for (const doc of otherDocuments) {
    if (!doc.file || !doc.name) continue;
    
    const fd = new FormData();
    fd.append('file', doc.file);
    const params = new URLSearchParams({ document_type_id: OTHER_DOCUMENT_TYPE_ID.toString() });
    const res = await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/documents?${params.toString()}`),
      {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      }
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      results.push({ error: err.detail || 'Upload failed', type_id: OTHER_DOCUMENT_TYPE_ID, name: doc.name });
    } else {
      results.push({ ...await res.json(), custom_name: doc.name });
    }
  }
  
  return results;
}

/** Map GET /employee-master/{id} or profile-360 response to form state. */
export function mapEmployeeMasterToForm(data) {
  const is360 = !!data.personal;
  const personal = is360 ? data.personal : {};
  const employment = is360 ? data.employment : {};
  const hierarchy = is360 ? data.hierarchy : data.hierarchy || {};
  const payroll = is360 ? data.payroll : data.payroll_profile || data.bank_details || {};
  const org = is360 ? {} : data.org_mapping || {};

  if (is360) {
    return {
      id: personal.id,
      emp_code: personal.emp_code,
      first_name: personal.first_name,
      middle_name: personal.middle_name,
      last_name: personal.last_name,
      email: personal.email,
      personal_email: personal.personal_email,
      official_email: personal.official_email,
      phone: personal.phone,
      alternate_phone: personal.alternate_phone,
      department_id: employment.department_id,
      designation_id: employment.designation_id,
      scale: employment.grade,
      emp_desig: employment.designation,
      join_date: employment.join_date,
      reporting_manager_id: hierarchy.reporting_manager_id,
      supervisor_id: hierarchy.supervisor_id,
      functional_manager_id: hierarchy.functional_manager_id,
      hospital_branch: employment.hospital_branch,
      work_location: employment.work_location,
      office_location_id: employment.office_location_id,
      employment_status: employment.employment_status,
      father_name: personal.father_name,
      mother_name: personal.mother_name,
      date_of_birth: personal.date_of_birth,
      sex: personal.gender,
      marital_status: personal.marital_status,
      blood_group: personal.blood_group,
      current_address: personal.current_address,
      permanent_address: personal.permanent_address,
      city: personal.city,
      state: personal.state,
      pin_code: personal.pin_code,
      pan_number: personal.pan_number,
      aadhaar_number: personal.aadhaar_number,
      bank_name: payroll.bank_name,
      bank_account_number: payroll.bank_account_number,
      bank_ifsc_code: payroll.bank_ifsc_code,
      bank_branch: payroll.bank_branch,
      bank_branch_code: payroll.bank_branch_code,
      emergency_contact_name: personal.emergency_contact_name,
      emergency_contact_number: personal.emergency_contact_number,
      emergency_contact_relationship: personal.emergency_contact_relationship,
      pension_scheme_type: payroll.pension_scheme_type || personal.pension_scheme_type,
      payroll_head_id: employment.payroll_head_id,
      is_disabled: personal.is_physically_disabled ? 'Yes' : (personal.is_physically_disabled === false ? 'No' : ''),
      is_physically_disabled: personal.is_physically_disabled,
      is_eligible_washing_allowance: payroll.is_eligible_washing_allowance,
      is_eligible_family_planning_allowance: payroll.is_eligible_family_planning_allowance,
    };
  }

  return {
    id: data.id,
    emp_code: data.emp_code,
    first_name: data.first_name,
    middle_name: data.middle_name,
    last_name: data.last_name,
    email: data.personal_email || data.email,
    personal_email: data.personal_email,
    official_email: data.official_email,
    phone: data.phone,
    alternate_phone: data.alternate_phone,
    employee_type: normalizeEmployeeType(data.employee_type) || data.employee_type,
    department_id: data.department_id,
    designation_id: data.designation_id,
    designation_type: data.designation_type,
    scale: data.scale,  // Legacy field
    scale_id: data.scale_id,
    basic_pay: data.basic_pay,
    grade_master_id: data.grade_master_id,
    emp_desig: data.designation_title || data.emp_desig,
    join_date: data.join_date,
    date_of_birth: data.date_of_birth,
    sex: data.sex,
    marital_status: data.marital_status,
    blood_group: data.blood_group,
    hospital_role_id: data.hospital_role_id,
    hospital_role: data.hospital_role_slug || (typeof data.hospital_role === 'string' ? data.hospital_role : ''),
    reporting_manager_id: data.reporting_manager_id || hierarchy.reporting_manager_id,
    supervisor_id: data.supervisor_id || hierarchy.supervisor_id,
    functional_manager_id: hierarchy.functional_manager_id,
    hospital_branch: hierarchy.hospital_branch,
    work_location: hierarchy.work_location,
    office_location_id: data.office_location_id,
    shift_id: data.shift_id,
    shift_group_id: org?.shift_group_id,
    roster_group_id: org?.roster_group_id,
    payroll_group_id: org?.payroll_group_id,
    leave_policy_master_id: org?.leave_policy_master_id,
    attendance_policy_master_id: org?.attendance_policy_master_id,
    pf_code: data.pf_code,
    hba_id: data.hba_id,
    opening_pf_balance: data.opening_pf_balance,
    opening_vpf_balance: data.opening_vpf_balance,
    opening_pf_balance_as_on: data.opening_pf_balance_as_on,
    is_physically_disabled: data.is_physically_disabled,
    is_disabled: data.is_physically_disabled ? 'Yes' : (data.is_physically_disabled === false ? 'No' : ''),
    is_eligible_washing_allowance: data.is_eligible_washing_allowance,
    is_eligible_family_planning_allowance: data.is_eligible_family_planning_allowance,
    payroll_head_id: data.payroll_head_id,
    is_eligible_conveyance_allowance: data.is_eligible_conveyance_allowance || false,
    current_address: data.current_address,
    permanent_address: data.permanent_address,
    city: data.city,
    state: data.state,
    pin_code: data.pin_code,
    pan_number: data.pan_number,
    aadhaar_number: data.aadhaar_number,
    pran_number: data.pran_number,
    uan_number: data.uan_number,
    esic_number: data.esic_number,
    father_name: data.father_name,
    mother_name: data.mother_name,
    pension_scheme_type: data.pension_scheme_type,
    emergency_contact_name: data.emergency_contact_name,
    emergency_contact_number: data.emergency_contact_number,
    emergency_contact_relationship: data.emergency_contact_relationship,
    bank_name: data.bank_details?.bank_name || data.bank_name,
    bank_account_number: data.bank_details?.bank_account_number || data.bank_account_number,
    bank_ifsc_code: data.bank_details?.bank_ifsc_code || data.bank_ifsc_code,
    bank_branch: data.bank_details?.bank_branch || data.bank_branch,
    bank_branch_code: data.bank_details?.bank_branch_code || data.bank_branch_code,
  };
}
